import { useState } from 'react';
import { Edit, Trash2, Clock, MessageSquare, AlertTriangle, Globe } from 'lucide-react';
import { formatTime, calculateDuration, formatDuration, isDayTime, utcToLocalTime } from '../utils/dateUtils';

export const SleepEntriesList = ({ entries, loading, onEdit, onDelete, childTimezone = null }) => {
  const [expandedEntry, setExpandedEntry] = useState(null);

  const toggleExpanded = (id) => {
    setExpandedEntry(expandedEntry === id ? null : id);
  };

  const getEntryTypeLabel = (type) => {
    return type === 'start' ? 'Schlafanfang' : 'Schlafende';
  };

  const getEntryTypeColor = (type) => {
    return type === 'start' 
      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  };

  const getEntryIcon = (type) => {
    return type === 'start' ? '🌙' : '☀️';
  };

  if (loading) {
    return (
      <div className="mobile-card text-center py-8">
        <div className="spinner mx-auto mb-3"></div>
        <p className="text-gray-500 dark:text-gray-400">Lade Einträge...</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="mobile-card text-center py-8">
        <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Keine Einträge für heute
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          Füge den ersten Schlaf-Eintrag hinzu.
        </p>
      </div>
    );
  }

  return (
    <div className="mobile-space-y">
      <h3 className="text-responsive-lg font-semibold text-gray-900 dark:text-white">
        Schlaf-Einträge
      </h3>

      <div className="space-y-3">
        {entries.map((entry) => {
          const startTime = entry.startTime ? new Date(entry.startTime) : null;
          const endTime = entry.endTime ? new Date(entry.endTime) : null;
          const duration = startTime && endTime ? calculateDuration(startTime, endTime) : null;
          
          // Convert to local timezone for display
          const entryTimezone = entry.timezone || childTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
          const localStartTime = startTime ? utcToLocalTime(startTime, entryTimezone) : null;
          const localEndTime = endTime ? utcToLocalTime(endTime, entryTimezone) : null;
          const isDay = localStartTime ? isDayTime(localStartTime, entryTimezone) : false;

          return (
            <div
              key={entry.id}
              className={`mobile-card ${
                entry.specialEvent 
                  ? 'border-l-4 border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10' 
                  : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Entry Type and Time */}
                  <div className="flex items-center space-x-2 mb-2">
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      entry.type === 'start'
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    }`}>
                      {entry.type === 'start' ? 'Eingeschlafen' : 'Aufgewacht'}
                    </div>
                    
                    {entry.specialEvent && (
                      <div className="flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">
                        <AlertTriangle className="w-3 h-3" />
                        <span>Besonderes Ereignis</span>
                      </div>
                    )}
                  </div>

                  {/* Time Display */}
                  <div className="flex items-center space-x-4 mb-2">
                    {localStartTime && (
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatTime(localStartTime, entryTimezone)}
                        </span>
                        {isDay && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            (Tag)
                          </span>
                        )}
                      </div>
                    )}
                    
                    {localEndTime && (
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatTime(localEndTime, entryTimezone)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Duration */}
                  {duration && (
                    <div className="mb-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Dauer: <span className="font-medium">{formatDuration(duration)}</span>
                      </span>
                    </div>
                  )}

                  {/* Timezone Info */}
                  {entry.timezone && (
                    <div className="flex items-center space-x-1 mb-2">
                      <Globe className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {entry.timezone}
                      </span>
                    </div>
                  )}

                  {/* Comment */}
                  {entry.comment && (
                    <div className="flex items-start space-x-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {entry.comment}
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => onEdit(entry)}
                    className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border border-blue-200 dark:border-blue-800"
                    aria-label="Eintrag bearbeiten"
                    title="Eintrag bearbeiten"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Eintrag wirklich löschen?')) {
                        onDelete(entry.id);
                      }
                    }}
                    className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border border-red-200 dark:border-red-800"
                    aria-label="Eintrag löschen"
                    title="Eintrag löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {entries.length > 0 && (
        <div className="mobile-card bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2">
            Zusammenfassung
          </h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Einträge:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {entries.length}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Besondere Ereignisse:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {entries.filter(e => e.specialEvent).length}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Zeitzonen:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {new Set(entries.map(e => e.timezone).filter(Boolean)).size}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Aktuelle Zeitzone:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {childTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 